import React, { type ReactNode } from "react";
import fr from "./fr";
import type { PriorityFaultEvent } from "diagnostics-domain/faultEvent";
import { capitalize } from "diagnostics-lib/string";
import type { CustomTranslations } from "../../../../i18n/types";

const en: typeof fr = {
  "project.event.active": "Active",
  "project.event.active.state": "State",
  "project.event.diagnosed_tab.blank_state.any_pi.action": "Access to settings",
  "project.event.diagnosed_tab.blank_state.any_pi.subtitle":
    "To view the evolution of the impact of this event on one or more performance indicators, add them in the project settings",
  "project.event.diagnosed_tab.blank_state.any_pi.title": "Unable to display the impact evolution",
  "project.event.header.button.activity.tooltip": "Event activity",
  "project.event.header.button.comments.tooltip": "Event comments",
  "project.event.header.card.action_identified.title.text": "Identified actions",
  "project.event.header.card.action_identified.update.text": "Update",
  "project.event.header.card.last_comment.access.text": "Access",
  "project.event.header.card.last_comment.empty.text": "Be the first to comment on this event.",
  "project.event.header.card.last_comment.title.text": "Last comment",
  "project.event.header.card.resume.duration.text": (
    duration: ReactNode,
    firstDetection: string,
    lastDetection: string | null,
  ) => {
    if (duration === null || lastDetection === null) {
      return <>First detected: {firstDetection}</>;
    }
    return (
      <>
        Duration: {duration} (first detected: {firstDetection} - last detected: {lastDetection})
      </>
    );
  },
  "project.event.header.card.resume.fault.text": (faultName: ReactNode, faultCount: number) => (
    <>
      {faultCount > 1 ? "Faults" : "Fault"}: <strong>{faultName}</strong>
    </>
  ),
  "project.event.header.card.resume.history.label": "History:",
  "project.event.header.card.resume.history.link": (historyCount: number) =>
    `${historyCount} similar${historyCount > 1 ? "s" : ""}`,
  "project.event.header.card.resume.see_more.text": "See more",
  "project.event.header.card.resume.title.text": "Summary",
  "project.event.header.description.section.action_plan.title": "Action Plan",
  "project.event.header.description.section.average_power_loss.title": "Average power losses",
  "project.event.header.description.section.average_power_loss.tooltip":
    "The average value is based on detections since the event beginning. The average value displayed uses the weighted loss values between 2 points in time and considers an absence of detection as a zero loss value.",
  "project.event.header.description.section.duration.title": "Duration",
  "project.event.header.description.section.fault.title": "Fault",
  "project.event.header.description.section.total_energy_losses.title": "Total energy losses",
  "project.event.header.description.section.total_energy_losses.tooltip":
    "The energy calculation integrates losses since the beginning of the event, considering an absence of detection as a zero loss value and prorating the losses based on the load factor on the period.",
  "project.event.inactive": "Inactive",
  "project.event.manual_fault.diagnosis.evolution.impact_time_window.metadata.actions.delete":
    "Delete",
  "project.event.manual_fault.diagnosis.evolution.impact_time_window.metadata.actions.edit":
    "Modify",
  "project.event.manual_fault.diagnosis.evolution.impact_time_window.metadata.actions.impossible_to_delete":
    "Impossible to delete this period as it's the only one that exists",
  "project.event.manual_fault.diagnosis.evolution.impact_time_window.metatadata.end_date":
    "Period end date",
  "project.event.manual_fault.diagnosis.evolution.impact_time_window.metatadata.start_date":
    "Period start date",
  "project.event.manual_fault.diagnosis.evolution.impact_time_window.modal.add.title": "Add period",
  "project.event.manual_fault.diagnosis.evolution.impact_time_window.modal.delete.title":
    "Delete period",
  "project.event.manual_fault.diagnosis.evolution.impact_time_window.modal.edit.title":
    "Modify period",
  "project.event.manual_fault.modalForm.time_window.delete.description":
    "Do you want to delete the period and associated impacts:",
  "project.event.manual_fault.modalForm.time_window.delete.message.warning.next_period":
    "Deleting this period will replace its impact values with those of the next period.",
  "project.event.manual_fault.modalForm.time_window.delete.message.warning.previous_period":
    "Deleting this period will replace its impact values with those of the previous period.",
  "project.event.manual_fault.modalForm.time_window.delete.success":
    "The period deletion was successful",
  "project.event.manual_fault.side_panel.details.anomaly": "Anomaly name",
  "project.event.manual_fault.side_panel.details.hitory.tooltip.info": () => (
    <>
      <strong>Fault</strong>: includes information related to the fault, as defined in the settings.{" "}
      <br />
      <strong>History</strong>: shows other manual events linked to the same anomaly (list
      restricted to production projects).
    </>
  ),
  "project.event.manual_fault.side_panel.history.blank_state.subtitle":
    "If a similar event is detected on a production project, the link to this event will appear here.",
  "project.event.manual_fault.side_panel.history.blank_state.title": "No similar events detected",
  "project.event.manual_fault.tab.diagnosis.evolution_chart.impact_time_windows_change.description":
    "The impact of the most recent period is automatically applied to new analyses until the event is closed.",
  "project.event.manual_fault.tab.diagnosis.evolution_chart.impact_time_windows_change.title":
    "Impact summary:",
  "project.event.manual_fault.tab.diagnosis.evolution_chart.impact_time_windows_change.title.button.add_time_window":
    "Period",
  "project.event.manual_fault.tables.impacts_time_windows_change.label": ({
    quantityName,
  }: {
    quantityName: string;
  }) => `Impact on ${quantityName}`,
  "project.event.side_panel.activity.project_link": "Open project activity",
  "project.event.side_panel.activity.title": "Event activity",
  "project.event.side_panel.comments.project_link": "Open project comments",
  "project.event.side_panel.comments.title": "Event comments",
  "project.event.side_panel.history.filter.detection": "Detection",
  "project.event.side_panel.history.filter.detection.label": ({
    label,
  }: {
    label: boolean;
  }): string => (label ? "True" : "False"),
  "project.event.side_panel.history.filter.fault_event_status": "Status",
  "project.event.side_panel.tab.history": (historyCount: number) => `History (${historyCount})`,
  "project.event.type.diagnosed": "Diagnosed",
  "project.event.type.manual": "Manual",
  "project.event.type.multi_fault": "Multi-fault event",
  "project.events.drawer.create_manual_event.closed.error": "End date must be after start date",
  "project.events.drawer.create_manual_event.closed.error.before_window":
    "End date must be after the beginning of the last time window",
  "project.events.drawer.create_manual_event.closed.start.error":
    "The end date must be between the start date and the end date of the data source",
  "project.events.drawer.create_manual_event.date_picker.closedAt.label": "End date",
  "project.events.drawer.create_manual_event.date_picker.startsAt.label": "Start date",
  "project.events.drawer.create_manual_event.description.error.max_characters":
    "The description cannot exceed 4000 characters.",
  "project.events.drawer.create_manual_event.error.manual_fault_name_already_exists":
    "A known issue already has this name (automatically created from an event). Choose a different name for the event.",
  "project.events.drawer.create_manual_event.error.overlapping_manual_fault_event":
    "Another event linked to this issue already covers this period. Modify the dates to avoid overlap.",
  "project.events.drawer.create_manual_event.input.name": "Event name",
  "project.events.drawer.create_manual_event.section.anomaly.description":
    "To ease tracking, associate this event with a known issue. Otherwise, a new type of issue will be created automatically with the event name.",
  "project.events.drawer.create_manual_event.section.anomaly.radio.label.no": "No",
  "project.events.drawer.create_manual_event.section.anomaly.radio.label.yes": "Yes",
  "project.events.drawer.create_manual_event.section.anomaly.select.impacted_system.label":
    "Impacted system",
  "project.events.drawer.create_manual_event.section.anomaly.select.label": "Known issue",
  "project.events.drawer.create_manual_event.section.anomaly.select.system.label": "System",
  "project.events.drawer.create_manual_event.section.anomaly.title": "Associate with a known issue",
  "project.events.drawer.create_manual_event.section.indicator_performance.title":
    "Impact on performance indicators",
  "project.events.drawer.create_manual_event.section.indicator_performance.title.tooltip":
    "This value will be applied to all analyses from the start date, as well as for future analyses. It can be reviewed within the event.",
  "project.events.drawer.create_manual_event.select.priority": "Priority",
  "project.events.drawer.create_manual_event.select.status": "Status",
  "project.events.drawer.create_manual_event.start.error":
    "The start date must be between the first and last data point linked to the data source",
  "project.events.drawer.create_manual_event.start.error.after_closed":
    "Start date must be before end date",
  "project.events.drawer.create_manual_event.start.error.after_window":
    "The start date must be before the next period start date",
  "project.events.drawer.create_manual_event.start.error.missing": "Start date is required",
  "project.events.drawer.create_manual_event.toast.success": (eventName: ReactNode) => (
    <>Manual event {eventName} has been created</>
  ),
  "project.events.manualFaultEvent.modal_form.add_period.toast.success":
    "The period has been successfully added",
  "project.events.manualFaultEvent.modifyModalForm.errors.should_be_digital": "Must be a number",
  "project.events.manualFaultEvent.modifyModalForm.impactedPIs":
    " Impact on performance indicators: ",
  "project.events.manualFaultEvent.modifyModalForm.impactedPeriod": "Impacted period: ",
  "project.events.manualFaultEvent.modifyModalForm.input.placeholder": "Value",
  "project.events.manualFaultEvent.modifyModalForm.toast.success":
    "Period was successfully updated",
  "project.events.manual_fault.impact_time_windows_change.datepicker.startsAt.label":
    "Period start date",
  "project.events.manual_fault.window.startsAt.error.after_latest_analysis": (formatDate: string) =>
    `The start date of the period must be before the date of the last analysis in the project (${formatDate})`,
  "project.events.manual_fault.window.startsAt.error.before_event_start": (formatDate: string) =>
    `The start date of the period must be after the start date of the event (${formatDate})`,
  "project.events.manual_fault.window.startsAt.error.next_existing_period": (formatDate: string) =>
    `The start date of the period must be before the start date of the next period in the event (${formatDate})`,
  "project.events.manual_fault.window.startsAt.error.previous_existing_period": (
    formatDate: string,
  ) =>
    `The start date of the period must be after the start date of the previous period in the event (${formatDate})`,
  "project.events.modalForm.addPeriod.description": "Enter the start date and impacts to apply:",
  "project.events.modalForm.okButton.add": "Add",
  "project.events.modalForm.okButton.delete": "Delete",
  "project.events.modalForm.okButton.modify": "Modify",
  "project.events.tag.manual": "Manual",
  "project.fault_event.badge.tooltips.single": "Only 1 detection",
  "project.fault_event.batch_status_change.modal.form.change_status.impacted_period":
    "Displayed period:",
  "project.fault_event.batch_status_change.modal.form.change_status.nb_impacted_detections":
    "Detections affected by the event:",
  "project.fault_event.chart.evolution.title": "Impact evolution",
  "project.fault_event.chart.evolution.title.bagde.closable": "Closable",
  "project.fault_event.chart.evolution.title.bagde.significative_increase": "Significant evolution",
  "project.fault_event.chart.magnitude.axis.title": (faultName: string, unit: string) =>
    `${faultName} amplitude (${unit})`,
  "project.fault_event.common.select.prompt": "Select",
  "project.fault_event.dashboard_select.create": (translations: CustomTranslations) =>
    `Create a ${translations.EN.DASHBOARD.toLowerCase()} (shared by default)`,
  "project.fault_event.dashboard_select.empty": (translations: CustomTranslations) =>
    `No ${translations.EN.DASHBOARD.toLowerCase()} has been shared in the project.`,
  "project.fault_event.detection.false": "False detection",
  "project.fault_event.detection.true": "True detection",
  "project.fault_event.diagnosis.symptoms.chart.observables": "Observables",
  "project.fault_event.diagnosis.symptoms.chart.performance_indicators": "Performance indicators",
  "project.fault_event.evolution_chart.batch_status_change.alert_icon.tooltip.linkText":
    "Relaunch the diagnosis",
  "project.fault_event.evolution_chart.batch_status_change.alert_icon.tooltip.text":
    "No diagnosis was relaunched following this false detection",
  "project.fault_event.evolution_chart.batch_status_change.change": "Change the status",
  "project.fault_event.evolution_chart.batch_status_change.displayed_period.description":
    "The summary and status changes apply only to the current event for the displayed period.",
  "project.fault_event.evolution_chart.batch_status_change.displayed_period.title": ({
    period,
  }: {
    period: ReactNode;
  }) => <>Detection status for the displayed period: {period}</>,
  "project.fault_event.evolution_chart.batch_status_change.modal.change_status.title":
    "Change the status",
  "project.fault_event.evolution_chart.batch_status_change.modal.form.additionnal_message.update_fault_status":
    (status: string) => (
      <>
        Following this action, the event status will be changed to &quot;<strong>{status}</strong>
        &quot; status.
      </>
    ),
  "project.fault_event.evolution_chart.batch_status_change.modal.form.additionnal_message.update_fault_status_and_closed_event":
    "Following this action, the event will be closed and marked as a false detection.",
  "project.fault_event.evolution_chart.batch_status_change.modal.form.change_status.button.name":
    "Change",
  "project.fault_event.evolution_chart.batch_status_change.modal.form.change_status.checkbox.apply_to_new_detections":
    "Automatically apply this status to future live detections.",
  "project.fault_event.evolution_chart.batch_status_change.modal.form.change_status.checkbox.apply_to_new_detections.disabled":
    "Option not available while status has not been changed",
  "project.fault_event.evolution_chart.batch_status_change.modal.form.change_status.relunch_diagnosis":
    "Relaunch a diagnosis without considering this fault.",
  "project.fault_event.evolution_chart.batch_status_change.modal.form.change_status.toast.update_status":
    "Statuses have been updated",
  "project.fault_event.evolution_chart.batch_status_change.modal.form.change_status.tooltip.apply_to_new_detections.already_set":
    "This status is already set as applied to future live detections.",
  "project.fault_event.evolution_chart.batch_status_change.no_detection": "No detection",
  "project.fault_event.evolution_chart.batch_status_change.reintegrate":
    "Reintegrate into the diagnosis",
  "project.fault_event.evolution_chart.batch_status_change.repeat_icon.tooltip.text":
    "Status is applied automatically to future live detections",
  "project.fault_event.evolution_chart.batch_status_change.status_applied_new_detections":
    "Status applied to future live detections:",
  "project.fault_event.evolution_chart.batch_status_change.title": ({
    status,
    count,
  }: {
    status: string;
    count: number;
  }) => `${status}: ${count}`,
  "project.fault_event.evolution_chart.change_fault_event_status.modal.form.toast.success":
    "Modifications saved successfully.",
  "project.fault_event.evolution_chart.detections_status_change.modal.form.additionnal_message.repeat_detection":
    "This status will be applied to upcoming detections.",
  "project.fault_event.evolution_chart.detections_status_change.modal.form.button.name": "Save",
  "project.fault_event.evolution_chart.detections_status_change.modal.form.description": ({
    toAnalysed,
  }: {
    toAnalysed: ReactNode;
  }) => (
    <>
      The status will be automatically applied to future live detections. Before any modification,
      the default status is {toAnalysed}.
    </>
  ),
  "project.fault_event.evolution_chart.detections_status_change.modal.title":
    "Status applied to future live detections",
  "project.fault_event.fault.chart.period_picker.reset": "Zoom in on the event",
  "project.fault_event.fault.evolution.overview.header.option.amplitude": "Amplitude",
  "project.fault_event.fault.evolution.overview.header.option.amplitude.description":
    "This display focuses on the evolution of the fault amplitude, as identified by the diagnosis algorithm.",
  "project.fault_event.fault.evolution.overview.header.option.impact": "Impact",
  "project.fault_event.fault.hearder.editing.name.error.cannot_be_empty":
    "Event name cannot be empty",
  "project.fault_event.fault.hearder.editing.name.error.cannot_exceed_100_characters":
    "The name cannot exceed 100 characters",
  "project.fault_event.fault.hearder.editing.name.error.must_be_unique":
    "An event with the same name already exists",
  "project.fault_event.fault.subtitle.date": (
    duration: string | null,
    firstDetection: string,
    lastDetection: string | null,
  ) => {
    if (duration === null || lastDetection === null) {
      return `First detected: ${firstDetection}`;
    }
    return `Duration: ${duration} (first detected: ${firstDetection} - last detected: ${lastDetection})`;
  },
  "project.fault_event.fault.subtitle.identified_actions": "Identified actions",
  "project.fault_event.fault.subtitle.name": "Fault:",
  "project.fault_event.header.badge_event_status.tooltip.text":
    "Click on the badge to edit the event status",
  "project.fault_event.header.button.comment": "Comment",
  "project.fault_event.header.button.edit": "Edit",
  "project.fault_event.header.last_event_comment.button.access_comment": "Access comments",
  "project.fault_event.header.last_event_comment.publishedAt.access_comment": ({
    relativeDate,
  }: {
    relativeDate: string;
  }) => `wrote ${relativeDate}`,
  "project.fault_event.header.link.history": "History",
  "project.fault_event.header.menu.option.merge": "Merge with another event",
  "project.fault_event.header.menu.option.merge.disabled":
    "No merge is possible because there are no other events related to this fault in the project.",
  "project.fault_event.header.menu.option.rename": "Edit name",
  "project.fault_event.header.menu.option.split": "Split the event in two",
  "project.fault_event.header.menu.option.split.disabled":
    "This event only lasts a day and cannot be split.",
  "project.fault_event.header.menu.option.split.disabled.multi_failure":
    "A multi-failure event cannot be split.",
  "project.fault_event.header.menu.option.split.disabled.status.closed":
    "This event is closed and cannot be modified.",
  "project.fault_event.header.menu.option.ungroup": "Ungroup",
  "project.fault_event.header.menu.tag.inactive_event": "Inactive",
  "project.fault_event.header.navigation.dashboard_tab.tooltip.icon.add_dashboard": (
    translations: CustomTranslations,
  ) => `Add a ${translations.EN.DASHBOARD.toLowerCase()}`,
  "project.fault_event.header.navigation.dashboard_tab.tooltip.icon.remove_dashboard": "Remove",
  "project.fault_event.header.navigation.dashboard_tab.tooltip.icon.see_more": "See more",
  "project.fault_event.header.navigation.modal_form.add_dashboard.select.prompt": "Select",
  "project.fault_event.header.navigation.modal_form.remove_dashboard.text": (
    {
      dashboardName,
    }: {
      dashboardName: ReactNode;
    },
    translations: CustomTranslations,
  ) => (
    <>
      Are you sure you want to remove the {translations.EN.DASHBOARD.toLowerCase()} {dashboardName}?{" "}
      <br /> This action won’t affect the {translations.EN.DASHBOARD.toLowerCase()} itself, but it
      will no longer be visible in this event.
    </>
  ),
  "project.fault_event.header.navigation.modal_form.remove_dashboard.title": (
    translations: CustomTranslations,
  ) => `Remove the ${translations.EN.DASHBOARD.toLowerCase()}`,
  "project.fault_event.live.widget.average_losses.title": ({
    translations,
  }: {
    translations: {
      FR: {
        LOSSES: string;
        AVERAGE: string;
      };
      EN: {
        LOSSES: string;
        AVERAGE: string;
      };
    };
  }) => `${capitalize(translations.EN.AVERAGE)} ${translations.EN.LOSSES.toLowerCase()}`,
  "project.fault_event.live.widget.average_losses.tooltip": (
    translations: {
      FR: {
        LOSSES: string;
        AVERAGE: string;
      };
      EN: {
        LOSS: string;
        AVERAGE: string;
      };
    },
    durationISO8601: string,
  ) => {
    const lossLower = translations.EN.LOSS.toLowerCase();

    return `The values displayed are calculated on the basis of the ${lossLower} for the analyses present over the ${durationISO8601}. The average value displayed uses the weighted ${lossLower} values between 2 points in time. The comparison is made with the value taken over the previous period of the same duration.`;
  },
  "project.fault_event.live.widget.average_losses.value.description": ({
    percent,
    total,
    unit,
  }: {
    percent: string;
    total: string;
    unit: string | null;
  }) => `${percent} % of ${total} ${unit} measured`,
  "project.fault_event.live.widget.average_losses.value.trend_indicator": ({
    evolution,
  }: {
    evolution: ReactNode;
  }) => <>{evolution} compared to the previous period</>,
  "project.fault_event.live.widget.event_priority.calculation_info": "No active events",
  "project.fault_event.live.widget.event_priority.empty": "No active event",
  "project.fault_event.live.widget.event_priority.title": "Event priorities",
  "project.fault_event.live.widget.event_priority.tooltip":
    "Only active events are taken into account in the calculation. The event priority is calculated based on several criteria (impact on the performance indicator, confidence level of the detection, detection frequency). It can be manually adjusted.",
  "project.fault_event.live.widget.losses_repartition.title"({
    translations,
  }: {
    translations: {
      FR: {
        LOSSES: string;
      };
      EN: {
        LOSSES: string;
      };
    };
  }) {
    return `${capitalize(translations.EN.LOSSES)} distribution`;
  },
  "project.fault_event.live.widget.losses_repartition.tooltip": (
    translations: {
      FR: {
        LOSSES: string;
        AVERAGE: string;
      };
      EN: {
        LOSS: string;
        AVERAGE: string;
      };
    },
    durationISO8601: string,
  ) => {
    const lossLower = translations.EN.LOSS.toLowerCase();
    const averageLower = translations.EN.AVERAGE.toLowerCase();

    return `The values displayed are calculated on the basis of the ${lossLower} for the analyses present over the ${durationISO8601}. They correspond to an aggregation per system of the ${averageLower} ${lossLower} for each event. Only the top 3 systems are presented.`;
  },
  "project.fault_event.loading": "The content is loading, and may take a few moments.",
  "project.fault_event.modal.form.change_status.comment.label": "Comment",
  "project.fault_event.modal.form.change_status.comment.placeholder": "Add a comment",
  "project.fault_event.modal.form.change_status.comment.sub_comment":
    "This comment will be saved in the event's comment thread.",
  "project.fault_event.modal.form.change_status.input.action_identified.error":
    "This field cannot exceed 4000 characters.",
  "project.fault_event.modal.form.change_status.input.action_identified.label":
    "Identified actions",
  "project.fault_event.modal.form.change_status.input.action_identified.placeholder":
    "Mention here the identified actions (e.g., link to the work request(s))",
  "project.fault_event.modal.form.datePicker.split_date.info": (date: ReactNode) => (
    <>Date must be between: {date}</>
  ),
  "project.fault_event.modal.form.datePicker.split_date.label": "Split date",
  "project.fault_event.modal.form.group_events.button.name.ok": "Group",
  "project.fault_event.modal.form.group_events.input.error.empty_field":
    "The event name must not be empty.",
  "project.fault_event.modal.form.group_events.input.name.hint":
    "The earliest start date suffix is added automatically.",
  "project.fault_event.modal.form.group_events.input.name.label": "Event group name",
  "project.fault_event.modal.form.group_events.summary.events": (count: number) =>
    `Events to group (${count})`,
  "project.fault_event.modal.form.group_events.summary.events.endsAt": "End date",
  "project.fault_event.modal.form.group_events.summary.events.name": "Name",
  "project.fault_event.modal.form.group_events.summary.events.startsAt": "Start date",
  "project.fault_event.modal.form.group_events.summary.name": (name: string) =>
    `Grouped event name: ${name}`,
  "project.fault_event.modal.form.group_events.toast.success":
    "The events have been successfully grouped.",
  "project.fault_event.modal.form.input.fault.name.error.cannot_exceed_100_characters":
    "The event name cannot exceed 100 characters",
  "project.fault_event.modal.form.merge_event.button.name.ok": "Merge",
  "project.fault_event.modal.form.merge_event.input.error.empty_field":
    "The event name must not be empty.",
  "project.fault_event.modal.form.merge_event.input.new_event.label": "Name of the new event",
  "project.fault_event.modal.form.merge_event.message.redirection":
    "After confirmation, you will be redirected to the new event page.",
  "project.fault_event.modal.form.merge_event.select.label": "Event to merge with",
  "project.fault_event.modal.form.merge_event.select.tooltip":
    "It is possible to merge with an event related to the same fault, located immediately before or after.",
  "project.fault_event.modal.form.merge_event.summary.comments": (
    comments: ReactNode,
    count: number,
  ) => (
    <>
      Comment{count === 0 || count > 1 ? "s" : ""}: {comments}
    </>
  ),
  "project.fault_event.modal.form.merge_event.summary.dashboards": (
    dashboard: ReactNode,
    count: number,
  ) => (
    <>
      Associated dashboard{count === 0 || count > 1 ? "s" : ""}: {dashboard}
    </>
  ),
  "project.fault_event.modal.form.merge_event.summary.date": (date: ReactNode) => (
    <>Dates: {date}</>
  ),
  "project.fault_event.modal.form.merge_event.summary.event_status": (status: ReactNode) => (
    <>Event status: {status}</>
  ),
  "project.fault_event.modal.form.merge_event.summary.name": (name: ReactNode) => <>Name: {name}</>,
  "project.fault_event.modal.form.merge_event.summary.newDiagnosesStatus": (status: ReactNode) => (
    <>Status applied to future live detections: {status}</>
  ),
  "project.fault_event.modal.form.merge_event.summary.reparability": (reparability: ReactNode) => (
    <>Outage time for repair: {reparability}</>
  ),
  "project.fault_event.modal.form.merge_event.summary.title": "Summary of the new event:",
  "project.fault_event.modal.form.merge_event.toast.success":
    "The events have been successfully merged.",
  "project.fault_event.modal.form.split_event.button.name.ok": "Split",
  "project.fault_event.modal.form.split_event.description": (eventName: ReactNode) => (
    <>
      You are about to split the event {eventName} in two. Choose the split date and define the
      names for each event.
    </>
  ),
  "project.fault_event.modal.form.split_event.input.error.empty_field":
    "The event name must not be empty.",
  "project.fault_event.modal.form.split_event.input.next_event.default_name": (name: string) =>
    `${name} - Part 2`,
  "project.fault_event.modal.form.split_event.input.next_event.hint":
    "New event, starting from the split date",
  "project.fault_event.modal.form.split_event.input.next_event.label": "Name of the second event",
  "project.fault_event.modal.form.split_event.input.previous_event.default_name": (name: string) =>
    `${name} - Part 1`,
  "project.fault_event.modal.form.split_event.input.previous_event.hint":
    "Existing event, retained until split date (excluded)",
  "project.fault_event.modal.form.split_event.input.previous_event.label":
    "Name of the first event",
  "project.fault_event.modal.form.split_event.message.redirection":
    "After confirmation, you will be redirected to the second event's page.",
  "project.fault_event.modal.form.split_event.toast.success":
    "The event has been successfully split into two.",
  "project.fault_event.modal.form.ungroup_event.button.name.ok": "Ungroup",
  "project.fault_event.modal.form.ungroup_event.summary.failures": "Failures concerned",
  "project.fault_event.modal.form.ungroup_event.summary.name": (name: ReactNode) => (
    <>Grouped event: {name}</>
  ),
  "project.fault_event.modal.form.ungroup_event.summary.source_fallback": (index: number) =>
    `Source event ${index}`,
  "project.fault_event.modal.form.ungroup_event.summary.sources":
    "Persisted source events that will be restored",
  "project.fault_event.modal.form.ungroup_event.toast.success":
    "The event has been successfully ungrouped.",
  "project.fault_event.modal.form.ungroup_event.warning.comments_lost":
    "Direct comments on the multi-failure event will be lost.",
  "project.fault_event.modal.merge_event.title": "Merge events",
  "project.fault_event.modal.split_event.title": "Split the event in two",
  "project.fault_event.modal.ungroup_event.title": "Ungroup event",
  "project.fault_event.modal_form.change_fault_event_status.selected.detection.false.not_all_faults_false_detection":
    "Not all detections are in false detection and once the event is closed, it will no longer be possible to change the status.",
  "project.fault_event.modal_form.change_fault_event_status.selected.detection.false.not_all_faults_false_detection_and_not_be_automatically_false_detection":
    ({ statusToNewDetection }: { statusToNewDetection: string }) => (
      <>
        Not all detections are in false detection and once the event is closed, it will no longer be
        possible to change the status. Also, the status applied to future live detections being
        &quot;
        {statusToNewDetection}&quot;, they will not be automatically put in false detection.
      </>
    ),
  "project.fault_event.modal_form.change_fault_event_status.selected.detection.false.not_be_automatically_false_detection":
    ({ statusToNewDetection }: { statusToNewDetection: string }) => (
      <>
        The status applied to future live detections being &quot;
        {statusToNewDetection}&quot;, they will not be automatically put in false detection.
      </>
    ),
  "project.fault_event.modal_form.change_fault_event_status.selected.detection.true.not_all_faults_confirmed":
    "Not all detections are confirmed and once the event is closed, it will no longer be possible to change the status.",
  "project.fault_event.modal_form.change_fault_event_status.selected.detection.true.not_all_faults_confirmed_and_not_be_automatically_confirmed":
    ({ statusToNewDetection }: { statusToNewDetection: string }) => (
      <>
        Not all detections are confirmed and once the event is closed, it will no longer be possible
        to change the status. Also, the status applied to future live detections being &quot;
        {statusToNewDetection}&quot;, they will not be automatically confirmed.
      </>
    ),
  "project.fault_event.modal_form.change_fault_event_status.selected.detection.true.not_be_automatically_confirmed":
    ({ statusToNewDetection }: { statusToNewDetection: string }) => (
      <>
        The status applied to future live detections being &quot;{statusToNewDetection}&quot;, they
        will not be automatically confirmed.
      </>
    ),
  "project.fault_event.modal_form.change_fault_event_status.selected.detection.true_or_false":
    "Are you sure you want to close this event as is?",
  "project.fault_event.modal_form.checkbox.detection_type.false_detection": "False detection",
  "project.fault_event.modal_form.select.detection_type.label": "Detection type",
  "project.fault_event.modal_form.select.outage_type_for_repair.label": "Outage type for repair",
  "project.fault_event.modal_form.select.status.label": "Status",
  "project.fault_event.modale.modal.change_status.title": "Change status",
  "project.fault_event.modale.modal.update_status.ok_button_name": "Change",
  "project.fault_event.priority.high": "High",
  "project.fault_event.priority.low": "Low",
  "project.fault_event.priority.medium": "Medium",
  "project.fault_event.quantity_selector.category.others": "Other",
  "project.fault_event.quantity_selector.category.promotion_filter": "Fault promotion filter",
  "project.fault_event.quantity_selector.switch.display_all_faults.tooltip.disabled":
    "Not available in mode Amplitude",
  "project.fault_event.quantity_selector.switch.display_key_quantities.label":
    "Display all quantities",
  "project.fault_event.quantity_selector.switch.display_key_quantities.off.tooltip.text":
    "By default, only performance indicators and the most impacted observables (top 8) are displayed.",
  "project.fault_event.quantity_selector.switch.display_key_quantities.on.tooltip.text":
    "By default, performance indicators, the most impacted observables (top 8), and all quantities used in their calculations are displayed.",
  "project.fault_event.side_panel.activity.title": "Activity",
  "project.fault_event.side_panel.details.category": "Category",
  "project.fault_event.side_panel.details.degradation_type": "Degradation type",
  "project.fault_event.side_panel.details.description": "Description",
  "project.fault_event.side_panel.details.equipment": "Equipment",
  "project.fault_event.side_panel.details.equipment_type": "Equipment type",
  "project.fault_event.side_panel.details.fault_name": "Fault name",
  "project.fault_event.side_panel.details.fault_type": "Promoted",
  "project.fault_event.side_panel.details.hitory.tooltip.info": () => (
    <>
      <strong>Fault</strong>: includes information related to the fault, as defined in the settings.{" "}
      <br />
      <strong>History</strong>: shows other events related to the same fault (limited to production
      projects and events with a status other than &quot;To analyze&quot;).
    </>
  ),
  "project.fault_event.side_panel.details.suspected_elements": "Suspected elements",
  "project.fault_event.side_panel.details.system": "System",
  "project.fault_event.side_panel.details.tooltip.fault_filter_type":
    "This fault is promoted when some quantities meet certain criteria, as defined in the setup.",
  "project.fault_event.side_panel.fault_event_details.input.name": "Event name",
  "project.fault_event.side_panel.fault_event_details.select.priority": "Priority",
  "project.fault_event.side_panel.fault_event_details.select.priority.hint": (priority: string) => (
    <>
      The priority was changed manually. The automatically calculated value is{" "}
      <strong>{priority}</strong>.
    </>
  ),
  "project.fault_event.side_panel.fault_event_details.select.priority.tooltip":
    "The event priority is calculated based on several criteria (impact on the performance indicator, confidence level of the detection, detection frequency). It can be manually adjusted.",
  "project.fault_event.side_panel.fault_event_details.title": "Event details",
  "project.fault_event.side_panel.history.blank_state.no_events_found.subtitle":
    "Try adjusting your filters.",
  "project.fault_event.side_panel.history.blank_state.no_events_found.title": "No events found",
  "project.fault_event.side_panel.history.blank_state.subtitle":
    "If a similar event with a status other than 'To be analyzed' is detected on a production project, the link to that event will appear here.",
  "project.fault_event.side_panel.history.blank_state.title": "No similar event detected",
  "project.fault_event.side_panel.history.card.start_date": ({
    isClosed,
    startDate,
    endDate,
  }: {
    isClosed: boolean;
    startDate: string;
    endDate: string;
  }) => (isClosed ? `${startDate} - ${endDate}` : `Start: ${startDate}`),
  "project.fault_event.side_panel.history.current_project": "This project",
  "project.fault_event.side_panel.tab.fault": (count: number): string =>
    count > 1 ? "Faults" : "Fault",
  "project.fault_event.side_panel.title": "Fault information",
  "project.fault_event.status.action_identified": "Action identified",
  "project.fault_event.status.closed": "Closed",
  "project.fault_event.status.on_going": "Analysis in progress",
  "project.fault_event.status.to_be_analyzed": "To be analyzed",
  "project.fault_event.tab.data": "Data",
  "project.fault_event.tab.fault": "Impact",
  "project.fault_event.tab.symptom": "Symptoms",
  "project.fault_event.tooltips.analysis_to_analyze": () => (
    <>This event contains at least a detection with the status &quot;To be analyzed&quot;</>
  ),
  "project.fault_event.tooltips.single": "Event with only 1 detection",
  "project.fault_events.button.export": "Export in .csv",
  "project.fault_events.charts.losses_repartition.all_around_zero":
    "Values present but too low to be displayed.",
  "project.fault_events.charts.losses_repartition.categories.system": "System",
  "project.fault_events.charts.losses_repartition.categories.total": "Internal losses",
  "project.fault_events.charts.losses_repartition.label.internal_losses": "Total",
  "project.fault_events.charts.losses_repartition.label.other_systems": "Others",
  "project.fault_events.charts.losses_repartition.label.unexplained_losses": (translations: {
    FR: {
      UNEXPLAINED: string;
    };
    EN: {
      UNEXPLAINED: string;
    };
  }) => `${capitalize(translations.EN.UNEXPLAINED)}`,
  "project.fault_events.closed.blank_state.subtitle":
    "Once the events are closed, they will be listed here",
  "project.fault_events.closed.blank_state.title": "No closed events",
  "project.fault_events.export.message.no_checkbox_selected":
    "Please select at least one event status to continue exporting.",
  "project.fault_events.grouping.button": (count: number) => `Group (${count})`,
  "project.fault_events.grouping.modal.title": "Group events",
  "project.fault_events.live.blank_state.subtitle": "Events to be analyzed here will appear here",
  "project.fault_events.live.blank_state.title": "No events to analyze",
  "project.fault_events.live.button.create_manual_event": "Manual event",
  "project.fault_events.live.section.priority.empty": (prioriry: PriorityFaultEvent): string => {
    if (prioriry === "HIGH") {
      return "No high-priority event";
    } else if (prioriry === "MEDIUM") {
      return "No medium-priority event";
    } else {
      return "No low-priority event";
    }
  },
  "project.fault_events.loading": "Loading in progress",
  "project.fault_events.modal.title.export": "Export events",
  "project.fault_events.modal_form.export.text": () => (
    <>
      Events (diagnosed and manual) and associated details will be exported as a .csv file.
      <br />
      Select the event statuses to be included in the exported file:
    </>
  ),
  "project.fault_events.selector.graph": "Graph",
  "project.fault_events.selector.graph_tooltip": "The graph view is currently in development.",
  "project.fault_events.selector.table": "Table",
  "project.fault_events.tab.closed": "Closed",
  "project.fault_events.tab.closed.detection": (detection: boolean): string =>
    detection ? "True" : "False",
  "project.fault_events.tab.in_progress": "In progress",
  "project.fault_events.table.average_losses.no_sparkline": (duration: string) =>
    `No detection over the ${duration}`,
  "project.fault_events.table.closed.metadata.detection": "Detection",
  "project.fault_events.table.closed.metadata.end_date": "Closing date",
  "project.fault_events.table.closed.metadata.name": "Name",
  "project.fault_events.table.closed.metadata.reparability": "Outage time for repair",
  "project.fault_events.table.closed.metadata.start_date": "First detection",
  "project.fault_events.table.closed.metadata.system": "System",
  "project.fault_events.table.live.action.ungroup": "Ungroup",
  "project.fault_events.table.live.average_losses.tooltip.info": (
    translations: {
      FR: {
        LOSSES: string;
      };
      EN: {
        LOSS: string;
      };
    },
    durationISO8601: string,
    showPartLoadSensitiveKpis: boolean,
  ) => {
    const lossLower = translations.EN.LOSS.toLowerCase();

    if (!showPartLoadSensitiveKpis) {
      return `The displayed curve is based on detections over the ${durationISO8601}.`;
    }

    return `The average value and the displayed curve are based on detections over the ${durationISO8601}. The average value displayed uses the weighted ${lossLower} values between 2 points in time and considers an absence of detection as a zero ${lossLower} value.`;
  },
  "project.fault_events.table.live.metadata.average_losses": (
    translations: {
      FR: {
        LOSSES: string;
        AVERAGE: string;
      };
      EN: {
        LOSSES: string;
        AVERAGE: string;
      };
    },
    duration: string,
  ) =>
    `${capitalize(translations.EN.AVERAGE)} ${translations.EN.LOSSES.toLowerCase()} over the ${duration.toLowerCase()}`,
  "project.fault_events.table.live.metadata.comments": "Comments",
  "project.fault_events.table.live.metadata.end_date": "Latest detection",
  "project.fault_events.table.live.metadata.last_activity_date": "Last activity",
  "project.fault_events.table.live.metadata.last_comment.tooltip.content": ({
    author,
    analysis,
    publishedAt,
    content,
  }: {
    author: string;
    analysis: string | null;
    publishedAt: string;
    content: ReactNode;
  }) => (
    <>
      {author} wrote {publishedAt} {analysis ? `about the analysis ${analysis} ` : ""}:
      <br />
      {content}
    </>
  ),
  "project.fault_events.table.live.metadata.last_comment.tooltip.title": "Last comment",
  "project.fault_events.table.live.metadata.name": "Name",
  "project.fault_events.table.live.metadata.name.icon.inactive.title": "Inactive event",
  "project.fault_events.table.live.metadata.name.tooltip.button.inactive_event": "Make inactive",
  "project.fault_events.table.live.metadata.reparability": "Outage time for repair",
  "project.fault_events.table.live.metadata.start_date": "First detection",
  "project.fault_events.table.live.metadata.status": "Status",
  "project.fault_events.table.live.metadata.system": "System",
  "project.fault_events.table.live.metadata.system.multiple": (nbSelection: number) =>
    `System${nbSelection > 1 ? "s" : ""}: `,
  "project.fault_events.table.live.metadata.work_request": "Identified actions",
  "project.fault_events.table.live.switch.cold_events.text": "Display inactive events",
  "project.fault_events.table.live.switch.cold_events.tooltip.text":
    "No activity over the last 3 months or only 1 detection",
  "project.fault_events.table.live.tooltips.closable":
    "Closable event (no detections linked to this event in the last three months)",
  "project.fault_events.table.live.tooltips.significant_evolution.high":
    "Significative increase of impact (raise of more than 20% between the last 10 detections and the 10 previous ones) ",
  "project.fault_events.table.live.tooltips.significant_evolution.low":
    "Significative drop of impact (drop of more than 20% between the last 10 detections and the 10 previous ones)",
  "project.fault_events.title": "Events",
  "project.fault_events.widgets.no_data_in_period_placeholder": "No data available for this period",
  "project.fault_events.widgets.no_data_placeholder": "No data available",
  "project.fault_events.widgets.no_pi_placeholder":
    "It is necessary to define at least one performance indicator to display the average loss widget.",
  "project.manual_fault_event.edit_form.system.info":
    "Changing this system updates the known issue used by every linked manual event.",
  "project.manual_fault_event.edit_form.system.label": "System",
  "project.manual_fault_event.header.menu.option.delete": "Delete",
  "project.manual_fault_event.header.modal_form.remove.success": (name: string) => (
    <>
      Deletion of <strong>{name}</strong> succeeded
    </>
  ),
  "project.manual_fault_event.modal.delete.title": "Delete manual event",
  "project.manual_fault_event.modal_form.remove.text": (name: ReactNode) => (
    <>
      You are about to permanently delete the event {name}. This event will no longer appear in the
      list of events, and the associated impact will no longer appear in the different graphs.
    </>
  ),
  "project.manual_fault_event.tag.manual": "Manual",
  "project.widget.average_losses.evolution.state": (
    evolutionState: "stable" | "improvement" | "deterioration",
  ): string => {
    switch (evolutionState) {
      case "stable":
        return "Stability";
      case "improvement":
        return "Improvement";
      case "deterioration":
        return "Deterioration";
      default:
        throw new Error("Invalid evolution state");
    }
  },
  "project.widget.average_losses.evolution.value": (evolutionValue: string): string =>
    `(previous period: ${evolutionValue})`,
  "projet.events.create_manual_fault_event.drawer.title": "Create manual event",
};

export default en;
